import React from "react";
import { Doughnut } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatNumber, getChartColors } from "./charts/chartTheme";
import LoadingOverlay from "./ui/LoadingOverlay";
import { Skeleton } from "./ui/Skeleton";
import { EmptyState, ErrorState } from "./ui/StateBlock";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// Payment method categories must be distinguishable from each other, so these are data colors
// (not decoration): green for cash, accent for card, then muted tones for the rest.
function paymentColors(colors) {
  return ["#2A9D75", colors.accent, "#D69A2D", "#D4504C", "#7C8DA6"];
}

/** Payment methods: a donut with percentages and a list of amounts next to it. */
export default function PaymentsDonut({ data, darkMode, loading, error, onRetry }) {
  const colors = getChartColors(darkMode);

  if (!data || data.length === 0) {
    if (error) return <ErrorState onRetry={onRetry} />;
    if (loading) {
      return (
        <div role="status" aria-label="Učitavanje" className="flex flex-col items-center gap-4 sm:flex-row">
          <Skeleton className="h-28 w-28 shrink-0 rounded-full" />
          <div className="flex w-full flex-col gap-3">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/5" />
            <Skeleton className="h-3.5 w-3/5" />
          </div>
        </div>
      );
    }
    return <EmptyState title="Nema plaćanja u izabranom periodu" hint="Probaj drugi period ili magacin." />;
  }

  const palette = paymentColors(colors);
  // Only payment methods that actually have payments in the period; the color stays tied to the method
  // (position in the full list), so it does not change when another method drops out.
  const rows = data
    .map((d, i) => ({ ...d, color: palette[i % palette.length] }))
    .filter((d) => Math.abs(parseFloat(d.sumpayvalue) || 0) >= 0.005);
  const total = rows.reduce((sum, d) => sum + (parseFloat(d.sumpayvalue) || 0), 0);

  if (rows.length === 0) {
    return <EmptyState title="Nema plaćanja u izabranom periodu" hint="Probaj drugi period ili magacin." />;
  }

  const chartData = {
    labels: rows.map((d) => d.paytypename),
    datasets: [
      {
        data: rows.map((d) => d.sumpayvalue),
        backgroundColor: rows.map((d) => d.color),
        borderWidth: 3,
        borderColor: colors.surface,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    cutout: "58%",
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#FBFCFD",
        font: { weight: "600", size: 9 },
        formatter: (value) => {
          const pct = total ? Math.round((value / total) * 100) : 0;
          return pct >= 12 ? `${pct}%` : "";
        },
      },
      // Amounts and shares are in the list next to the donut; a tooltip in a small canvas would be clipped.
      tooltip: { enabled: false },
    },
  };

  return (
    <LoadingOverlay loading={loading}>
      <div className="flex items-center gap-4">
        <div style={{ width: 104, height: 104 }} className="relative shrink-0 overflow-hidden">
          <Doughnut data={chartData} options={options} />
        </div>
        <ul className="flex min-w-0 flex-1 flex-col gap-2">
          {rows.map((d) => (
            <li key={d.paytypename} className="min-w-0 text-sm leading-tight">
              <span className="flex min-w-0 items-center gap-2 text-slate-600 dark:text-slate-300">
                <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: d.color }} />
                <span className="truncate">{d.paytypename}</span>
              </span>
              <span className="mt-0.5 block whitespace-nowrap pl-[18px] tabular-nums">
                <span className="font-semibold text-slate-900 dark:text-slate-50">{formatNumber(d.sumpayvalue)}</span>{" "}
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  RSD{total ? ` \u00b7 ${Math.round(((parseFloat(d.sumpayvalue) || 0) / total) * 100)}%` : ""}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </LoadingOverlay>
  );
}
